import React, { useState, useEffect } from 'react';

/**
 * A React hook for dynamically importing modules with a loading placeholder state
 * Dynamic imports allow code to be loaded on demand rather than all at once when the app loads.
 * This helps reduce the initial bundle size and improve page load performance.
 * When using dynamic imports, webpack automatically code splits the imported modules into separate chunks
 * that are loaded asynchronously only when needed.
 * This is especially useful for large components or features that aren't immediately needed on page load.
 * The separate chunks are downloaded in parallel with the main bundle, reducing the overall load time.
 *
 * @param {Function} modulePromise - A function that returns a dynamic import promise (e.g. () => import('./MyComponent'))
 * @param {Object} initialState - The initial placeholder state while the module loads. Should match the shape of the imported module.
 * @returns {Object} The loaded module or the initial placeholder state while loading
 *
 * @example
 * // Import a single default export
 * const { default: MyComponent } = useDynamicImport(
 *   () => import('./MyComponent'),
 *   { default: () => <Placeholder /> }
 * );
 *
 * @example
 * // Import multiple named exports
 * const { ComponentA, ComponentB } = useDynamicImport(
 *   () => import('./components'),
 *   {
 *     ComponentA: () => <Placeholder />,
 *     ComponentB: () => <Placeholder />
 *   }
 * );
 */
export function useDynamicImport(modulePromise, initialState) {
  const [result, setResult] = useState(initialState);

  useEffect(() => {
    modulePromise()
      .then(setResult)
      .catch(console.error);
  }, []);

  return result;
}

/**
 * A placeholder component that shows an animated loading state while content is being dynamically imported.
 * Useful for providing visual feedback during dynamic imports.
 *
 * @param {Object} props
 * @param {boolean} [props.inert] - If true, disables the loading animation
 * @param {string|number} [props.width] - Width of the placeholder
 * @param {boolean|string} [props.round] - If true, makes the placeholder circular. If string, sets border radius
 * @param {string|number} [props.height] - Height of the placeholder
 * @param {Object} [props.style] - Additional CSS styles to apply
 * @param {string} [props.backgroundColor='#f5f5f5'] - Background color of the placeholder
 * @returns {JSX.Element} A div element styled as a placeholder
 *
 * @example
 * // Basic usage
 * <Placeholder width="200px" height="100px" />
 *
 * @example
 * // Circular placeholder
 * <Placeholder width={40} height={40} round />
 */
export function Placeholder({
  inert,
  width,
  round,
  height,
  style,
  backgroundColor = '#f5f5f5',
}) {
  return (
    <div
      className="dynamic-import-placeholder"
      style={{
        width,
        height,
        backgroundColor,
        minWidth: width,
        minHeight: height,
        borderRadius: round === true ? '100%' : (round || 'initial'),
        animation: inert ? 'none' : 'dynamic-import-placeholder-animation 1s alternate infinite',
        ...style,
      }}
    />
  );
}
